/* istanbul ignore file */
/* eslint-disable */
import React from "react";
import "./scenario-table.scss";
import {
  InkomensanalyseResultaat,
  ToelichtingRegel,
  MotiveringCompleetOutput,
  InkomensanalyseResultaatAanvrager,
  InkomensanalyseConclusieOpties,
  ToelichtingOptions,
} from "../../../.generated/rapportage/rapportagetypes";
import { TableNettoBesteedbaarInkomen, NbiItem } from "./nbi";
import { bedragFormatNull, monthDivision } from "./infra/scenario-utils";
import {
  excludedFootnotes,
  CitationProvider,
  CitationNote,
  CitationRef,
} from "./citation-notes";
import { ControleMaandlastOptions, getNaam } from "./infra/utils";
import {
  ControleGewenstInkomenOptions,
  DashboardScenarioWerkloosheid,
} from "../../../.generated/rapportage/rapportagetypes";
import { optellen } from "../../../shared/utils/currency";
import Table from "../components/Table";
import { Column } from "../components/BaseTable";
import { BenodigdKapitaalTable } from "./tables/benodigd-kapitaal-table";
import { ControleMaandlastTable } from "./tables/controle-maandlast-table";
import { MaximaleHypotheekTable } from "./tables/maximale-hypotheek";
import Heading from "../components/Heading";
import { BenodigdInkomenTable } from "./tables/benodigd-inkomen-table";
import { ControleWerkelijkeLastenTable } from "./tables/controle-werkelijke-lasten-table";

// Helper function (updated to use the new getInkomstenPeriodes function)
function getInkomstenPeriodes(
  motiveringDl: MotiveringCompleetOutput,
  partnerIsSelected: boolean
) {
  const periodResults = motiveringDl.motiveringInkomensanalyse?.resultaten;

  const aanvrager1PeriodData: (
    | InkomensanalyseResultaatAanvrager
    | null
    | undefined
  )[] = [undefined, undefined, undefined];
  const aanvrager2PeriodData: (
    | InkomensanalyseResultaatAanvrager
    | null
    | undefined
  )[] = [undefined, undefined, undefined];
  const periodConclusies: (InkomensanalyseConclusieOpties | null)[] = [];

  if (periodResults) {
    for (let i = 0; i < 3; i++) {
      // Assuming max 3 periods
      const currentPeriod: InkomensanalyseResultaat | undefined =
        periodResults[i];
      if (currentPeriod) {
        aanvrager1PeriodData[i] = partnerIsSelected
          ? currentPeriod.aanvrager2
          : currentPeriod.aanvrager1;
        aanvrager2PeriodData[i] = partnerIsSelected
          ? currentPeriod.aanvrager1
          : currentPeriod.aanvrager2;
        periodConclusies[i] = currentPeriod.conclusie;
      }
    }
  }

  return {
    aanvragerInkomstenPeriodeEerst: aanvrager1PeriodData[0],
    aanvragerInkomstenPeriodeTweede: aanvrager1PeriodData[1],
    aanvragerInkomstenPeriodeDerde: aanvrager1PeriodData[2],
    partnerInkomstenPeriodeEerste: aanvrager2PeriodData[0],
    partnerInkomstenPeriodeTweede: aanvrager2PeriodData[1],
    partnerInkomstenPeriodeDerde: aanvrager2PeriodData[2],
    conclusiePeriodeEerste: periodConclusies[0],
    conclusiePeriodeTweede: periodConclusies[1],
    conclusiePeriodeDerde: periodConclusies[2],
  };
}

export type InkomensanalyseTabelWerkloosheidProps = {
  motiveringOutput: MotiveringCompleetOutput | null;
  dashboardSettings: DashboardScenarioWerkloosheid;
  selectedKlantId?: string | null;
  selectedAanvrager?: "Aanvrager1" | "Aanvrager2";
  loading?: boolean;
};

export const InkomensanalyseTabelWerkloosheid: React.FC<InkomensanalyseTabelWerkloosheidProps> = ({
  motiveringOutput,
  dashboardSettings,
  selectedAanvrager,
  loading,
}) => {
  if (loading) {
    // Or some other loading indicator
    return <div>Laden...</div>;
  }

  if (!motiveringOutput) {
    return null; // Or some placeholder if motiveringOutput is essential
  }

  // Derive display flags from dashboardSettings (similar to overlijden-drawer.tsx)
  const nbiWeergeven =
    (dashboardSettings.uitgangspunten?.controleGewenstInkomen
      ?.controleGewenstInkomen === ControleGewenstInkomenOptions.Nbi &&
      !!dashboardSettings.uitgangspunten?.controleHypotheeknorm &&
      !!dashboardSettings.uitgangspunten?.controleGewenstInkomen
        ?.gewijzigdeJarenTonen) ||
    false;
  const benodigdKapitaalWeergeven =
    dashboardSettings?.uitgangspunten?.controleGewenstInkomen
      ?.benodigdKapitaalTonen || false;
  const controleWerkelijkeLastenWeergeven =
    (dashboardSettings?.uitgangspunten?.controleHypotheeknorm
      ?.controleMaandlast ||
      false) &&
    dashboardSettings?.uitgangspunten?.controleHypotheeknorm
      ?.controleMaandlast === ControleMaandlastOptions.WerkelijkeLast;
  const brutoInkomensWensenWeergeven =
    dashboardSettings?.uitgangspunten?.controleGewenstInkomen
      ?.controleGewenstInkomen === "BrutoInkomenswens";
  const brutoInkomensWensPercentage =
    dashboardSettings?.uitgangspunten?.controleGewenstInkomen
      ?.brutoInkomenswensPercentage || 0;
  const controleMaandlast =
    dashboardSettings?.uitgangspunten?.controleHypotheeknorm
      ?.controleMaandlast || false;
  const controleMaandlastKeuze =
    dashboardSettings?.uitgangspunten?.controleHypotheeknorm
      ?.controleMaandlast || null;
  const maximaleHypotheekWeergeven =
    dashboardSettings?.uitgangspunten?.controleHypotheeknorm
      ?.controleerMaximaleHypotheek || false;

  // --- Start of data mapping logic from mapMotiveringWWToInkomensanalyseProps ---
  const normGegevens = motiveringOutput.normGegevens;
  const motiveringInkomensanalyse = motiveringOutput.motiveringInkomensanalyse;

  const partnerIsSelected = selectedAanvrager === "Aanvrager2";

  const aanvrager =
    selectedAanvrager === "Aanvrager2"
      ? motiveringOutput.motiveringInkomensanalyse?.aanvrager2
      : motiveringOutput.motiveringInkomensanalyse?.aanvrager1;

  // Define names similar to overlijden-drawer.tsx pattern
  const unemployedPersonName =
    selectedAanvrager === "Aanvrager2"
      ? motiveringOutput?.klantNaamPartner || "Partner"
      : motiveringOutput?.klantNaamAanvrager || "Aanvrager";

  const aanvragerName =
    selectedAanvrager === "Aanvrager1"
      ? motiveringOutput?.klantNaamAanvrager || "Aanvrager"
      : motiveringOutput?.klantNaamPartner || "Partner";

  const partnerName =
    selectedAanvrager === "Aanvrager2"
      ? motiveringOutput?.klantNaamAanvrager || "Aanvrager"
      : motiveringOutput?.klantNaamPartner || "Partner";

  const {
    aanvragerInkomstenPeriodeEerst,
    aanvragerInkomstenPeriodeTweede,
    aanvragerInkomstenPeriodeDerde,
    partnerInkomstenPeriodeEerste,
    partnerInkomstenPeriodeTweede,
    partnerInkomstenPeriodeDerde,
    conclusiePeriodeEerste,
  } = getInkomstenPeriodes(motiveringOutput, partnerIsSelected);

  const conclusieText = conclusiePeriodeEerste || "";
  const inkomensanalyseResultaten =
    motiveringOutput.motiveringInkomensanalyse?.resultaten || [];

  // Transform nbiData for TableNettoBesteedbaarInkomen
  const rawNbiData =
    motiveringOutput.motiveringInkomensanalyse?.nbiOverzichten?.["1"]?.jaren ||
    [];

  const nbiData: NbiItem[] = rawNbiData.map((jaar) => ({
    ...jaar,
    bijzonderheden: jaar.bijzonderheden
      ? jaar.bijzonderheden.map((b) =>
          b.bijzonderheid === null ? null : { bijzonderheid: b.bijzonderheid }
        )
      : null,
  }));

  const nbiVoetnootText =
    motiveringOutput.motiveringInkomensanalyse?.nbiOverzicht?.voetnoot
      ?.voetnoot || "";
  const totaalHuidigBrutoJaarinkomenBedrag =
    motiveringInkomensanalyse?.totaalHuidigBrutoJaarinkomenBedrag;

  // Default to empty/null if critical data is missing for calculations
  if (
    !inkomensanalyseResultaten[0] ||
    !aanvragerInkomstenPeriodeEerst ||
    !normGegevens
  ) {
    // Or render a message indicating missing data
    return <div>Benodigde gegevens voor analyse ontbreken.</div>;
  }

  // Herbruikbare functie voor het berekenen van belegging inkomen per periode
  const berekenBeleggingInkomen = (inkomsten: any) => {
    return (
      (inkomsten?.beleggingenBedrag || 0) +
      (inkomsten?.beleggingenBruteringBedrag || 0)
    );
  };

  // Herbruikbare functie voor het berekenen van verhuur inkomen per periode
  const berekenVerhuurInkomen = (inkomsten: any) => {
    return (
      (inkomsten?.verhuurBedrag || 0) + (inkomsten?.verhuurBruteringBedrag || 0)
    );
  };

  // Herbruikbare functie voor het berekenen van inkomsten overig aanvrager per periode
  const berekenInkomstenOverigAanvragerPeriode = (
    aanvragerInkomsten: InkomensanalyseResultaatAanvrager | undefined | null
  ) => {
    return (
      (aanvragerInkomsten?.inkomstenOverigBedrag || 0) +
      (aanvragerInkomsten?.inkomstenOverigBruteringBedrag || 0)
    );
  };

  // Herbruikbare functie voor het berekenen van inkomsten overig partner per periode
  const berekenInkomstenOverigPartnerPeriode = (
    partnerInkomsten: InkomensanalyseResultaatAanvrager | undefined | null
  ) => {
    return (
      (partnerInkomsten?.inkomstenOverigBedrag || 0) +
      (partnerInkomsten?.inkomstenOverigBruteringBedrag || 0)
    );
  };

  // Herbruikbare functie voor het berekenen van inkomensverlies per periode (werkloosheid)
  const berekenInkomensverliesWerkloosheid = (
    aanvragerInkomsten: any,
    partnerInkomsten: any,
    inkomensanalyseResultaat: any
  ) => {
    return Math.max(
      optellen([
        (totaalHuidigBrutoJaarinkomenBedrag || 0) / 12 -
          ((aanvragerInkomsten?.restInkomenBedrag || 0) +
            berekenBeleggingInkomen(aanvragerInkomsten) +
            berekenVerhuurInkomen(aanvragerInkomsten) +
            (aanvragerInkomsten?.wwUitkeringBedrag || 0) +
            (partnerInkomsten?.restInkomenBedrag || 0) +
            berekenBeleggingInkomen(partnerInkomsten) +
            berekenVerhuurInkomen(partnerInkomsten) +
            berekenInkomstenOverigAanvragerPeriode(aanvragerInkomsten) +
            (aanvragerInkomsten?.woonlastenverzekeringBedrag || 0) +
            (aanvragerInkomsten?.woonlastenverzekeringBruteringBedrag || 0)) / 12,
        ]) || 0,
      0
    );
  };

  const inkomensData = {
    naamPersoon: unemployedPersonName,
    beleggingInkomenEerste: berekenBeleggingInkomen(
      aanvragerInkomstenPeriodeEerst
    ),
    beleggingInkomenTweede: berekenBeleggingInkomen(
      aanvragerInkomstenPeriodeTweede
    ),
    beleggingInkomenDerde: berekenBeleggingInkomen(
      aanvragerInkomstenPeriodeDerde
    ),
    verhuurInkomenEerste: berekenVerhuurInkomen(aanvragerInkomstenPeriodeEerst),
    verhuurInkomenTweede: berekenVerhuurInkomen(
      aanvragerInkomstenPeriodeTweede
    ),
    verhuurInkomenDerde: berekenVerhuurInkomen(aanvragerInkomstenPeriodeDerde),
    eersteUitkering: aanvragerInkomstenPeriodeEerst.wwUitkeringBedrag,
    tweedeUitkering: aanvragerInkomstenPeriodeTweede?.wwUitkeringBedrag || 0,
    restinkomen: aanvragerInkomstenPeriodeEerst?.restInkomenBedrag,
    aantalmaandenWwUitkering: aanvrager?.aantalMaandenWwUitkering || 0,
    // totalEerste, Tweede, Na are not directly in mapper output like this, they come from inkomensanalyseResultaten
    inkomensverliesEerste: berekenInkomensverliesWerkloosheid(
      aanvragerInkomstenPeriodeEerst,
      partnerInkomstenPeriodeEerste,
      inkomensanalyseResultaten[0]
    ),
    inkomensverliesTweede: berekenInkomensverliesWerkloosheid(
      aanvragerInkomstenPeriodeTweede,
      partnerInkomstenPeriodeTweede,
      inkomensanalyseResultaten[1]
    ),
    inkomensverliesDerde: berekenInkomensverliesWerkloosheid(
      aanvragerInkomstenPeriodeDerde,
      partnerInkomstenPeriodeDerde,
      inkomensanalyseResultaten[2]
    ),
    woonlastenEerstePeriode:
      (aanvragerInkomstenPeriodeEerst?.woonlastenverzekeringBedrag || 0) +
      (aanvragerInkomstenPeriodeEerst?.woonlastenverzekeringBruteringBedrag || 0),
    woonlastenTweedePeriode:
      (aanvragerInkomstenPeriodeTweede?.woonlastenverzekeringBedrag || 0) +
      (aanvragerInkomstenPeriodeTweede?.woonlastenverzekeringBruteringBedrag || 0),
    woonlastenDerdePeriode:
      (aanvragerInkomstenPeriodeDerde?.woonlastenverzekeringBedrag || 0) +
      (aanvragerInkomstenPeriodeDerde?.woonlastenverzekeringBruteringBedrag || 0),
    inkomenPartnerEerstePeriode:
      partnerInkomstenPeriodeEerste?.restInkomenBedrag || null,
    inkomenPartnerTweedePeriode:
      partnerInkomstenPeriodeTweede?.restInkomenBedrag || null,
    inkomenPartnerDerdePeriode:
      partnerInkomstenPeriodeDerde?.restInkomenBedrag || null,
    inkomstenOverigeEerstePeriode: berekenInkomstenOverigAanvragerPeriode(
      aanvragerInkomstenPeriodeEerst
    ),
    inkomstenOverigePartnerEerstePeriode: berekenInkomstenOverigPartnerPeriode(
      partnerInkomstenPeriodeEerste
    ),
    inkomstenOverigeTweedePeriode: berekenInkomstenOverigAanvragerPeriode(
      aanvragerInkomstenPeriodeTweede
    ),
    inkomstenOverigePartnerTweedePeriode: berekenInkomstenOverigPartnerPeriode(
      partnerInkomstenPeriodeTweede
    ),
    inkomstenOverigeDerdePeriode: berekenInkomstenOverigAanvragerPeriode(
      aanvragerInkomstenPeriodeDerde
    ),
    inkomstenOverigePartnerDerdePeriode: berekenInkomstenOverigPartnerPeriode(
      partnerInkomstenPeriodeDerde
    ),
  };

  const jaarlijkseAnalyse = {
    gemiddeldeInkomen: motiveringOutput.totaalBrutoInkomenGemiddeldBedrag,
    gewenstInkomen: normGegevens?.gewenstBrutoInkomenBedrag,
    // gewenstInkomenPercentage: // This is brutoInkomensWensPercentage from dashboardSettings
    //   ((normGegevens?.gewenstBrutoInkomenBedrag || 1) /
    //     (inkomensAnalyseResultaatDl.totaalHuidigBrutoJaarinkomenBedrag || 1)) * // This part would also need motiveringInkomensanalyse
    //   100,
    maandelijksTekort: normGegevens?.gewenstBrutoInkomenBedrag // Check this calculation
      ? Math.max(
          (normGegevens.gewenstBrutoInkomenBedrag -
            (inkomensanalyseResultaten[0]?.totaalBrutoInkomenGemiddeldBedrag ||
              0)) /
            12,
          0
        )
      : 0,
  };

  // Financial gegevens are mostly taken from inkomensanalyseResultaten[0] in the JSX
  // So we don't need to create a separate 'financieleGegevens' object like in the mapper if JSX is kept same.

  const voetnotenList = motiveringOutput.toelichtingRegels || [];
  const benodigdKapitaalBedrag =
    motiveringOutput.benodigdKapitaal?.bedrag || null;
  const benodigdKapitaalOmschrijvingText =
    motiveringOutput.benodigdKapitaal?.omschrijving || "";

  // --- End of data mapping logic ---

  const columnStyleLabel = {
    width: "40%",
  };
  const columnStyle = {
    width: "20%",
    textAlign: "right" as const,
  };

  if (!motiveringInkomensanalyse) {
    return null;
  }

  const incomeColumns: Column[] = [
    {
      header: "Periode",
      accessor: "period",
      align: "right",
    },
    {
      header: `Restinkomen ${aanvragerName}`,
      accessor: "restinkomen",
      dataType: "currency" as const,
      filterNullAnd0: false,
    },
    ...(inkomensData?.beleggingInkomenEerste !== 0 ||
    inkomensData?.beleggingInkomenTweede !== 0 ||
    inkomensData?.beleggingInkomenDerde !== 0
      ? [
          {
            header: (
              <>
                Inkomsten uit belegging(en) {aanvragerName}{" "}
                <CitationRef type={ToelichtingOptions.Brutering} />
              </>
            ),
            accessor: "beleggingInkomen",
            dataType: "currency" as const,
          },
        ]
      : []),
    ...(inkomensData?.verhuurInkomenEerste !== 0 ||
    inkomensData?.verhuurInkomenTweede !== 0 ||
    inkomensData?.verhuurInkomenDerde !== 0
      ? [
          {
            header: <>Inkomsten uit verhuur {aanvragerName}</>,
            accessor: "verhuurInkomen",
            dataType: "currency" as const,
          },
        ]
      : []),
    {
      header: "WW-uitkering",
      accessor: "wwUitkering",
      dataType: "currency" as const,
    },
    {
      header: "Woonlastenverzekering",
      accessor: "woonlastenverzekering",
      dataType: "currency" as const,
    },
    {
      header: `Inkomen ${partnerName}`,
      accessor: "inkomenPartner",
      dataType: "currency" as const,
    },
    {
      header: (
        <>
          Inkomsten overig {aanvragerName}{" "}
          <CitationRef type={ToelichtingOptions.Brutering} />
        </>
      ),
      accessor: "inkomstenOverig",
      dataType: "currency" as const,
    },
    {
      header: `Inkomsten overig ${partnerName}`,
      accessor: "inkomstenOverigPartner",
      dataType: "currency" as const,
    },
    {
      header: "Inkomen totaal",
      accessor: "totaalInkomen",
      dataType: "currency" as const,
      className: "font-weight-bold",
    },
    {
      header: "Inkomensverlies",
      accessor: "inkomensverlies",
      dataType: "currency" as const,
      className: "table-warning",
      violation: (value: number) => value > 0,
    },
  ];

  const incomeData = [
    {
      period: "1ste 2 maanden",
      restinkomen: inkomensData?.restinkomen
        ? inkomensData?.restinkomen / 12
        : 0,
      beleggingInkomen: inkomensData?.beleggingInkomenEerste
        ? inkomensData?.beleggingInkomenEerste / 12
        : 0,
      verhuurInkomen: inkomensData?.verhuurInkomenEerste
        ? inkomensData?.verhuurInkomenEerste / 12
        : 0,
      wwUitkering: inkomensData?.eersteUitkering
        ? inkomensData?.eersteUitkering / 12
        : 0,
      woonlastenverzekering: inkomensData?.woonlastenEerstePeriode
        ? inkomensData?.woonlastenEerstePeriode / 12
        : 0,
      inkomenPartner: inkomensData?.inkomenPartnerEerstePeriode
        ? inkomensData?.inkomenPartnerEerstePeriode / 12
        : 0,
      inkomstenOverig: inkomensData?.inkomstenOverigeEerstePeriode
        ? inkomensData?.inkomstenOverigeEerstePeriode / 12
        : 0,
      inkomstenOverigPartner: inkomensData?.inkomstenOverigePartnerEerstePeriode
        ? inkomensData?.inkomstenOverigePartnerEerstePeriode / 12
        : 0,
      totaalInkomen: inkomensanalyseResultaten?.[0]?.totaalBrutoInkomenBedrag
        ? inkomensanalyseResultaten?.[0]?.totaalBrutoInkomenBedrag / 12
        : 0,
      inkomensverlies: Math.abs(inkomensData?.inkomensverliesEerste || 0),
    },
    {
      period:
        (aanvrager?.aantalMaandenWwUitkering || 0) > 0
          ? `Na 2 t/m ${aanvrager?.aantalMaandenWwUitkering} maanden`
          : "Na 2 t/m onbekend aantal maanden",
      restinkomen: inkomensData?.restinkomen
        ? inkomensData?.restinkomen / 12
        : 0,
      beleggingInkomen: inkomensData?.beleggingInkomenTweede
        ? inkomensData?.beleggingInkomenTweede / 12
        : 0,
      verhuurInkomen: inkomensData?.verhuurInkomenTweede
        ? inkomensData?.verhuurInkomenTweede / 12
        : 0,
      wwUitkering: inkomensData?.tweedeUitkering
        ? inkomensData?.tweedeUitkering / 12
        : 0,
      woonlastenverzekering: inkomensData?.woonlastenTweedePeriode
        ? inkomensData?.woonlastenTweedePeriode / 12
        : 0,
      inkomenPartner: inkomensData?.inkomenPartnerTweedePeriode
        ? inkomensData?.inkomenPartnerTweedePeriode / 12
        : 0,
      inkomstenOverig: inkomensData?.inkomstenOverigeTweedePeriode
        ? inkomensData?.inkomstenOverigeTweedePeriode / 12
        : 0,
      inkomstenOverigPartner: inkomensData?.inkomstenOverigePartnerTweedePeriode
        ? inkomensData?.inkomstenOverigePartnerTweedePeriode / 12
        : 0,
      totaalInkomen: inkomensanalyseResultaten?.[1]?.totaalBrutoInkomenBedrag
        ? inkomensanalyseResultaten?.[1]?.totaalBrutoInkomenBedrag / 12
        : 0,
      inkomensverlies: Math.abs(inkomensData?.inkomensverliesTweede || 0),
    },
    {
      period: "Na WW uitkering",
      restinkomen: inkomensData?.restinkomen
        ? inkomensData?.restinkomen / 12
        : 0,
      beleggingInkomen: inkomensData?.beleggingInkomenDerde
        ? inkomensData?.beleggingInkomenDerde / 12
        : 0,
      verhuurInkomen: inkomensData?.verhuurInkomenDerde
        ? inkomensData?.verhuurInkomenDerde / 12
        : 0,
      wwUitkering: 0,
      woonlastenverzekering: inkomensData?.woonlastenDerdePeriode
        ? inkomensData?.woonlastenDerdePeriode / 12
        : 0,
      inkomenPartner: inkomensData?.inkomenPartnerDerdePeriode
        ? inkomensData?.inkomenPartnerDerdePeriode / 12
        : 0,
      inkomstenOverig: inkomensData?.inkomstenOverigeDerdePeriode
        ? inkomensData?.inkomstenOverigeDerdePeriode / 12
        : 0,
      inkomstenOverigPartner: inkomensData?.inkomstenOverigePartnerDerdePeriode
        ? inkomensData?.inkomstenOverigePartnerDerdePeriode / 12
        : 0,
      totaalInkomen: inkomensanalyseResultaten?.[2]?.totaalBrutoInkomenBedrag
        ? inkomensanalyseResultaten?.[2]?.totaalBrutoInkomenBedrag / 12
        : 0,
      inkomensverlies: Math.abs(inkomensData?.inkomensverliesDerde || 0),
    },
  ];

  const filteredIncomeData = incomeData;

  // Add these column definitions after the incomeColumns definition
  const standardColumns: Column[] = [
    {
      header: "Omschrijving",
      accessor: (row) => row.description,
      align: "left",
    },
    {
      header: "",
      accessor: "empty1",
      dataType: "currency",
    },
    {
      header: "",
      accessor: "amount",
      dataType: "currency",
    },
    {
      header: "",
      accessor: "empty2",
      dataType: "currency",
    },
  ];

  return (
    <CitationProvider
      voetnoten={voetnotenList}
      excludedFootnotes={excludedFootnotes}
    >
      <div className="container-fluid mt-2 px-2">
        {!loading && inkomensData && (
          <Heading
            level={3}
            text={`Inkomen bij werkloosheid van ${inkomensData.naamPersoon}`}
            ignoreNumbers
            ignoreTOC
          />
        )}
        <Table
          columns={incomeColumns}
          data={filteredIncomeData}
          id="inkomen-werkloosheid-scenario-table"
          className="scenario__table mb-4 table table-striped table-bordered table-hover table-responsive"
          rotate
          regularStyles={filteredIncomeData.length > 1}
        />
        <Heading
          level={3}
          text="Wat gebeurt er het eerste volledige kalenderjaar bij werkloosheid?"
          ignoreNumbers
          ignoreTOC
        />
        <Table
          columns={[
            {
              header: "Uw gemiddelde inkomen",
              accessor: (row) => row.amount,
              align: "right",
            },
          ]}
          data={[
            {
              description: "Uw gemiddelde inkomen",
              amount: bedragFormatNull(
                jaarlijkseAnalyse?.gemiddeldeInkomen,
                monthDivision
              ),
            },
          ]}
          className="scenario__table mb-4 table table-striped table-bordered table-hover table-responsive"
          rotate
        />
        {brutoInkomensWensenWeergeven && (
          <Table
            columns={standardColumns}
            data={[
              brutoInkomensWensenWeergeven
                ? [
                    {
                      description: "",
                    },
                    {
                      description: `Gewenst inkomen van minimaal ${brutoInkomensWensPercentage}% van huidig bruto inkomen`,
                      amount: bedragFormatNull(
                        (totaalHuidigBrutoJaarinkomenBedrag || 0) *
                          ((brutoInkomensWensPercentage || 0) / 100),
                        monthDivision
                      ),
                    },
                    {
                      description: "Maandelijks tekort aan gewenste inkomsten",
                      amount: bedragFormatNull(
                        Math.abs(jaarlijkseAnalyse?.maandelijksTekort || 0)
                      ),
                      rowOptions: {
                        className: "table-danger font-weight-bold",
                      },
                    },
                  ]
                : [],
            ]}
            className="scenario__table mb-4 table table-striped table-bordered table-hover table-responsive"
          />
        )}

        {controleWerkelijkeLastenWeergeven && (
          <ControleWerkelijkeLastenTable
            toegestaneLastBedragen={[
              inkomensanalyseResultaten?.[0]?.toegestaneLastBedrag ?? null,
              inkomensanalyseResultaten?.[1]?.toegestaneLastBedrag ?? null,
              inkomensanalyseResultaten?.[2]?.toegestaneLastBedrag ?? null,
            ]}
            werkelijkeLastBedragen={[
              inkomensanalyseResultaten?.[0]?.werkelijkeLastBedrag ?? null,
              inkomensanalyseResultaten?.[1]?.werkelijkeLastBedrag ?? null,
              inkomensanalyseResultaten?.[2]?.werkelijkeLastBedrag ?? null,
            ]}
            tekortToegestaneLastBedragen={[
              inkomensanalyseResultaten?.[0]?.tekortToegestaneLastBedrag ?? null,
              inkomensanalyseResultaten?.[1]?.tekortToegestaneLastBedrag ?? null,
              inkomensanalyseResultaten?.[2]?.tekortToegestaneLastBedrag ?? null,
            ]}
          />
        )}

        {controleMaandlast &&
          controleMaandlastKeuze ===
            ControleMaandlastOptions.BenodigdInkomen && (
            <BenodigdInkomenTable
              data={[
                {
                  benodigdInkomenBedrag:
                    inkomensanalyseResultaten?.[0]?.benodigdInkomenBedrag ??
                    null,
                  tekortBenodigInkomenBedrag:
                    inkomensanalyseResultaten?.[0]
                      ?.tekortBenodigInkomenBedrag ?? null,
                  tekortNettoInkomenBedrag:
                    inkomensanalyseResultaten?.[0]?.tekortNettoInkomenBedrag ??
                    null,
                },
              ]}
            />
          )}

        {maximaleHypotheekWeergeven && (
          <MaximaleHypotheekTable
            inkomensanalyseResultaten={inkomensanalyseResultaten}
          />
        )}

        {controleMaandlast && (
          <ControleMaandlastTable
            conclusies={[conclusieText]}
            controleMaandlastKeuze={controleMaandlastKeuze}
          />
        )}

        <div className="mt-3 px-2">
          {voetnotenList?.map((footnote: ToelichtingRegel) => (
            <CitationNote
              key={footnote.type}
              type={footnote.type}
              text={footnote.toelichting || ""}
            />
          ))}
        </div>

        <br />
        <br />

        {/* nbi */}
        {nbiWeergeven && (
          <TableNettoBesteedbaarInkomen
            startDatum={motiveringInkomensanalyse?.startdatum}
            nbi={nbiData}
            voetnoot={nbiVoetnootText}
          />
        )}

        {benodigdKapitaalWeergeven && (
          <BenodigdKapitaalTable
            benodigdKapitaal={benodigdKapitaalBedrag}
            benodigdKapitaalOmschrijving={benodigdKapitaalOmschrijvingText}
          />
        )}
      </div>
    </CitationProvider>
  );
};
